import { Star, Quote, ExternalLink } from "lucide-react";
import { Reveal, SectionHeading } from "./shared";

// Curated customer reviews — edit this list with your real Google review quotes & names.
const REVIEWS = [
  {
    name: "Marcus Tan",
    role: "MacBook Pro · Battery",
    text: "Fixed my MacBook battery same day and charged less than Apple quoted. Honest advice, no upselling. Highly recommend.",
  },
  {
    name: "Priya Nair",
    role: "Dell Laptop · Screen",
    text: "Cracked screen replaced within a few hours. Kept me updated on WhatsApp the whole time. Super convenient near Kembangan MRT.",
  },
  {
    name: "Jonathan Lee",
    role: "Gaming PC · No boot",
    text: "My custom PC wouldn't turn on. They diagnosed a PSU fault, quoted upfront and had it running that evening. Genuine experts.",
  },
  {
    name: "Siti Rahmah",
    role: "Windows Laptop · Slow",
    text: "Old laptop felt brand new after their SSD upgrade. Boots in seconds now. Friendly, quick and very fair pricing.",
  },
  {
    name: "David Wong",
    role: "iMac · Virus removal",
    text: "Cleaned up a nasty malware infection without wiping my data. Explained everything clearly. This is my go-to repair shop now.",
  },
  {
    name: "Aisyah K.",
    role: "Refurbished Laptop",
    text: "Bought a refurbished laptop here — spotless condition and great value. Set it up for me before I left. Excellent service.",
  },
];

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/search/?api=1&query=AFZ+Tech+Solution+52+Jalan+Kembangan+Singapore";

const Stars = ({ className = "w-4 h-4" }) => (
  <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className={`${className} fill-brand-lime text-brand-lime`} />
    ))}
  </div>
);

export const Reviews = () => (
  <section id="reviews" data-testid="reviews-section" className="relative py-24 sm:py-32 bg-navy/40 overflow-hidden">
    <div className="absolute -top-24 left-1/3 w-[26rem] h-[26rem] rounded-full bg-brand-lime/8 blur-[150px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="Trusted In Singapore"
        title="Rated 5 stars by"
        accent="our customers."
        sub="Real feedback from people across Singapore whose devices we've brought back to life."
      />

      {/* Rating summary */}
      <Reveal delay={0.1}>
        <div className="mt-12 flex flex-col sm:flex-row items-center gap-6 bg-card/80 border border-slate-800 rounded-2xl p-7 clip-corner">
          <div className="flex items-center gap-4">
            <span className="font-display text-6xl font-black text-brand-lime leading-none text-glow-lime">4.9</span>
            <div>
              <Stars className="w-5 h-5" />
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mt-2">
                Based on verified reviews
              </p>
            </div>
          </div>
          <div className="sm:ml-auto flex items-center gap-4">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Reviewed on Google</span>
            <a
              data-testid="reviews-google-cta"
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-brand-lime/50 text-brand-lime font-display font-bold uppercase text-sm px-5 py-2.5 rounded-full hover:bg-brand-lime hover:text-void transition-all duration-300"
            >
              <ExternalLink className="w-4 h-4" /> Leave a Review
            </a>
          </div>
        </div>
      </Reveal>

      {/* Review cards */}
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.07}>
            <div
              data-testid={`review-card-${i}`}
              className="group h-full bg-card/80 border border-slate-800 rounded-2xl p-7 hover:border-brand-lime/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_35px_rgba(170,255,0,0.12)] flex flex-col"
            >
              <div className="flex items-center justify-between">
                <Stars />
                <Quote className="w-7 h-7 text-brand-lime/25 group-hover:text-brand-lime/50 transition-colors" />
              </div>
              <p className="mt-4 text-sm text-slate-300 leading-relaxed flex-1">"{r.text}"</p>
              <div className="mt-6 flex items-center gap-3 pt-5 border-t border-slate-800">
                <div className="w-10 h-10 rounded-full bg-brand-lime/10 border border-brand-lime/40 flex items-center justify-center font-display font-bold text-brand-lime">
                  {r.name.charAt(0)}
                </div>
                <div>
                  <p className="font-display text-sm font-bold uppercase text-white leading-none">{r.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-brand-cyan mt-1.5">{r.role}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
